// src/components/CreateUserModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';
import { usersAPI, organizationsAPI } from '../services/api';

const CreateUserModal = ({ show, onHide, onUserCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    organization_id: null
  });
  
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Загрузка организаций для выбора
  useEffect(() => {
    if (show) {
      loadOrganizations();
      // Сброс формы при открытии
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'user',
        organization_id: null
      });
      setError('');
      setSuccess('');
    }
  }, [show]);

  const loadOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const response = await organizationsAPI.getOrganizations({
        skip: 0,
        limit: 100,
        only_active: true
      });
      setOrganizations(response.items || []);
    } catch (err) {
      console.error('Error loading organizations:', err);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Очищаем ошибку при изменении поля
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Валидация
    if (!formData.name.trim()) {
      setError('Имя пользователя обязательно');
      return;
    }
    
    if (!formData.email.trim()) {
      setError('Email обязателен');
      return;
    }
    
    if (!formData.password || formData.password.length < 6) {
      setError('Пароль должен содержать минимум 6 символов');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const userData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        role: formData.role,
        organization_id: formData.organization_id || null
      };
      
      console.log('📤 Creating user:', userData);
      
      const newUser = await usersAPI.createUser(userData);
      
      setSuccess(`Пользователь "${newUser.name}" успешно создан!`);
      
      // Сбрасываем форму
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'user',
        organization_id: null
      });
      
      // Уведомляем родительский компонент
      if (onUserCreated) {
        onUserCreated(newUser);
      }
      
      // Автоматически закрываем через 2 секунды
      setTimeout(() => {
        onHide();
      }, 2000);
      
    } catch (err) {
      console.error('❌ Error creating user:', err);
      
      let errorMessage = 'Не удалось создать пользователя';
      
      if (err.response?.status === 400) {
        errorMessage = err.response.data?.detail || 'Пользователь с таким именем или email уже существует';
      } else if (err.response?.status === 422) {
        const errors = err.response.data?.detail;
        if (Array.isArray(errors)) {
          errorMessage = errors.map(e => `${e.loc?.join('.')}: ${e.msg}`).join('; ');
        } else {
          errorMessage = 'Ошибка валидации данных';
        }
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { value: 'user', label: 'Пользователь' },
    { value: 'moderator', label: 'Модератор' },
    { value: 'admin', label: 'Администратор' }
  ];

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title className="d-flex align-items-center">
          <i className="bi bi-person-plus-fill text-primary me-2 fs-4"></i>
          Создание пользователя
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3" dismissible onClose={() => setError('')}>
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-3">
            <i className="bi bi-check-circle me-2"></i>
            {success}
          </Alert>
        )}
        
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Имя пользователя <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Введите имя пользователя"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  disabled={loading}
                  required
                  autoFocus
                />
                <Form.Text className="text-muted">
                  Логин для входа в систему
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={12}>
              <Form.Group>
                <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="email"
                  placeholder="user@example.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={loading}
                  required
                />
                <Form.Text className="text-muted">
                  Email будет использоваться для входа
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>Пароль <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Минимум 6 символов"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  disabled={loading}
                  required
                />
                <Form.Text className="text-muted">
                  Пароль должен содержать минимум 6 символов
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>Роль пользователя</Form.Label>
                <Form.Select
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  disabled={loading}
                >
                  {roleOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  <i className="bi bi-info-circle me-1"></i>
                  {formData.role === 'admin' && 'Полный доступ ко всем функциям системы'}
                  {formData.role === 'moderator' && 'Может управлять анкетами и просматривать аналитику'}
                  {formData.role === 'user' && 'Базовый доступ: заполнение анкет, просмотр профиля'}
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={12}>
              <Form.Group>
                <Form.Label>Организация</Form.Label>
                {loadingOrgs ? (
                  <div className="d-flex align-items-center gap-2 py-2">
                    <Spinner animation="border" size="sm" />
                    <span className="text-muted">Загрузка организаций...</span>
                  </div>
                ) : (
                  <Form.Select
                    value={formData.organization_id || ''}
                    onChange={(e) => handleChange('organization_id', e.target.value ? parseInt(e.target.value) : null)}
                    disabled={loading}
                  >
                    <option value="">Без организации</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Form.Text className="text-muted">
                  Выберите организацию, к которой будет привязан пользователь
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      
      <Modal.Footer className="border-top">
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Отмена
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={loading || !formData.name || !formData.email || !formData.password}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Создание...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-2"></i>
              Создать пользователя
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateUserModal;